import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Building2, 
  Users, 
  Home, 
  MessageSquare, 
  CreditCard,
  TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/dashboard`, {
        headers: getAuthHeader()
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-navy-200 rounded w-1/4" />
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-navy-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const subscriptions = data?.subscriptions || {};

  return (
    <div className="p-6 lg:p-8" data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-heading font-bold text-navy-900">Admin Dashboard</h1>
        <p className="text-navy-600">Platform overview and management</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-navy-600 mb-1">Total Agencies</p>
                <p className="text-3xl font-bold text-navy-900">{stats.total_agencies || 0}</p>
                <p className="text-xs text-green-600 mt-1">{stats.active_agencies || 0} active</p>
              </div>
              <div className="w-12 h-12 bg-sky-500 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-navy-600 mb-1">Total Customers</p>
                <p className="text-3xl font-bold text-navy-900">{stats.total_customers || 0}</p>
                <p className="text-xs text-green-600 mt-1">{stats.active_customers || 0} active</p>
              </div>
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-navy-600 mb-1">Total Properties</p>
                <p className="text-3xl font-bold text-navy-900">{stats.total_properties || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-navy-600 mb-1">Total Enquiries</p>
                <p className="text-3xl font-bold text-navy-900">{stats.total_enquiries || 0}</p>
              </div>
              <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscriptions & Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Subscription Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-sky-500" />
              Subscription Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-navy-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-gray-400 rounded-full" />
                  <span className="font-medium text-navy-900">Free</span>
                </div>
                <span className="text-2xl font-bold text-navy-900">{subscriptions.free || 0}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-blue-500 rounded-full" />
                  <span className="font-medium text-navy-900">Basic</span>
                </div>
                <span className="text-2xl font-bold text-navy-900">{subscriptions.basic || 0}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-purple-500 rounded-full" />
                  <span className="font-medium text-navy-900">Premium</span>
                </div>
                <span className="text-2xl font-bold text-navy-900">{subscriptions.premium || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Agencies */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-sky-500" />
              Recent Registrations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.recent_agencies?.length === 0 ? (
              <p className="text-navy-500 text-center py-8">No recent registrations</p>
            ) : (
              <div className="space-y-3">
                {data?.recent_agencies?.map((agency) => (
                  <div key={agency.id} className="flex items-center justify-between p-3 bg-navy-50 rounded-lg">
                    <div>
                      <p className="font-medium text-navy-900">{agency.company_name}</p>
                      <p className="text-sm text-navy-500">{agency.email}</p>
                    </div>
                    <Badge className={agency.subscription === 'premium' ? 'bg-purple-500' : agency.subscription === 'basic' ? 'bg-blue-500' : 'bg-gray-500'}>
                      {agency.subscription}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notifications Alert */}
      {data?.unread_notifications > 0 && (
        <Card className="mt-6 border-orange-200 bg-orange-50">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-navy-900">You have {data.unread_notifications} unread notifications</p>
                <p className="text-sm text-navy-600">Check the notifications page for details</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
